import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Info, Check, X, History, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import Skeleton from "@/components/jea/Skeleton";
import JeaToggle from "@/components/jea/JeaToggle";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { relativeTime, centsToUSD, shortDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

const PriceCell = ({ value, disabled, onSave, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState((value / 100).toFixed(2));
  const [saving, setSaving] = useState(false);
  const [warning, setWarning] = useState("");

  useEffect(() => {
    setDraft((value / 100).toFixed(2));
  }, [value]);

  const commit = async () => {
    const num = Number(draft);
    if (!Number.isFinite(num) || num < 0 || num > 9999.99) {
      toast.error("Enter a price between 0 and $9,999.99");
      return;
    }
    setSaving(true);
    try {
      const cents = Math.round(num * 100);
      await onSave(cents);
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setDraft((value / 100).toFixed(2));
    setEditing(false);
    setWarning("");
  };

  if (disabled) {
    return (
      <span className="jea-mono text-text-muted opacity-50" title="Broker disabled">
        ${(value / 100).toFixed(2)}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="jea-mono text-accent-pink font-semibold hover:bg-accent-pink/10 px-2 py-1 rounded-[4px] transition-colors"
        data-testid={testid}
      >
        ${(value / 100).toFixed(2)}
      </button>
    );
  }

  return (
    <div className="inline-flex items-center gap-1" data-testid={`${testid}-editor`}>
      <span className="text-text-secondary">$</span>
      <input
        autoFocus
        type="number"
        step="0.01"
        min="0"
        max="9999.99"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") cancel();
        }}
        className="w-24 h-8 px-2 rounded-[6px] bg-bg-tertiary border border-accent-pink text-white jea-mono text-[13px] font-semibold focus:outline-none focus:shadow-pink-glow"
      />
      <button
        type="button"
        onClick={commit}
        disabled={saving}
        className="w-7 h-7 rounded-[6px] bg-accent-pink text-[#091424] hover:bg-accent-pink-light inline-flex items-center justify-center disabled:opacity-50"
        data-testid={`${testid}-save`}
      >
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
      </button>
      <button
        type="button"
        onClick={cancel}
        className="w-7 h-7 rounded-[6px] bg-bg-tertiary border border-border-default text-text-secondary hover:text-white inline-flex items-center justify-center"
        data-testid={`${testid}-cancel`}
      >
        <X size={12} />
      </button>
    </div>
  );
};

const HistoryModal = ({ broker, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!broker) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/admin/brokers/${broker.id}/history`);
        if (alive) setEvents(data.events || []);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [broker?.id]);

  const fieldLabel = (f) => ({
    price_standard_cents: "Standard Rate",
    price_pro_cents: "PRO Rate",
    enabled: "Status",
  })[f] || f;

  const fmtValue = (field, val) => {
    if (field === "enabled") return val ? "Enabled" : "Disabled";
    return centsToUSD(Number(val) || 0);
  };

  return (
    <AnimatePresence>
      {broker && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[96] flex items-center justify-center px-4"
            data-testid="broker-history-modal"
          >
            <div
              className="w-full max-w-[640px] max-h-[85vh] overflow-y-auto bg-bg-secondary border border-border-default rounded-[16px] p-8 shadow-elevated"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-5">
                <div>
                  <div className="jea-caption">{broker.name}</div>
                  <h2 className="text-white font-bold text-[20px]">Pricing History</h2>
                </div>
                <button type="button" onClick={onClose} className="p-2 text-text-secondary hover:text-white">
                  <X size={16} />
                </button>
              </div>
              {loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                </div>
              ) : events.length === 0 ? (
                <div className="text-text-muted text-[13px] py-6 text-center">No changes recorded yet.</div>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-default">
                      <th className="jea-caption text-text-secondary p-3 text-left">Date</th>
                      <th className="jea-caption text-text-secondary p-3 text-left">Field</th>
                      <th className="jea-caption text-text-secondary p-3 text-left">Old</th>
                      <th className="jea-caption text-text-secondary p-3 text-left">New</th>
                      <th className="jea-caption text-text-secondary p-3 text-left">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.map((e) => (
                      <tr key={e.id} className="border-b border-border-default text-[13px]">
                        <td className="p-3 jea-date">{shortDate(e.created_at)}</td>
                        <td className="p-3 text-white">{fieldLabel(e.metadata?.field)}</td>
                        <td className="p-3 jea-mono text-text-secondary">{fmtValue(e.metadata?.field, e.metadata?.old_value)}</td>
                        <td className="p-3 jea-mono text-white">{fmtValue(e.metadata?.field, e.metadata?.new_value)}</td>
                        <td className="p-3 text-text-secondary">{e.admin_name || "System"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const AdminBrokersPage = () => {
  const [brokers, setBrokers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyBroker, setHistoryBroker] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/brokers");
      setBrokers(data.brokers || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (broker, body) => {
    try {
      const { data } = await apiClient.patch(`/admin/brokers/${broker.id}`, body);
      setBrokers((prev) => prev.map((b) => (b.id === broker.id ? data.broker : b)));
      const kind = body.enabled !== undefined ? "status" : "price";
      toast.success(kind === "status"
        ? (body.enabled ? "Broker enabled." : "Broker disabled.")
        : "Rate updated.");
      // Soft warning when PRO > Standard
      if (data.broker.price_pro_cents > data.broker.price_standard_cents) {
        toast.warning("PRO rate is higher than Standard — this is unusual.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-brokers-page">
      <div className="space-y-1">
        <div className="jea-caption">Pricing Control</div>
        <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Broker Management</h1>
      </div>

      <div className="rounded-[8px] bg-bg-tertiary border border-border-default p-4 flex items-start gap-2" data-testid="brokers-info-panel">
        <Info size={14} className="text-accent-pink shrink-0 mt-[3px]" />
        <p className="text-text-secondary text-[13px] leading-6">
          Broker pricing changes take effect immediately. Existing query runs retain their historical
          prices — only new queries use the new rate.
        </p>
      </div>

      {loading ? (
        <JeaCard className="p-4 space-y-2">
          {Array.from({ length: 7 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
        </JeaCard>
      ) : (
        <JeaCard className="p-0 overflow-hidden" data-testid="admin-brokers-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-default">
                  <th className="jea-caption text-text-secondary p-4 text-left">Broker</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Category</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Standard Rate</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">PRO Rate</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Status</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Last Updated</th>
                  <th className="jea-caption text-text-secondary p-4 text-right">History</th>
                </tr>
              </thead>
              <tbody>
                {brokers.map((b, i) => {
                  const disabled = !b.enabled;
                  const inversion = b.price_pro_cents > b.price_standard_cents;
                  return (
                    <motion.tr
                      key={b.id}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      transition={{ duration: 0.18, delay: i * 0.03 }}
                      className={cn(
                        "border-b border-border-default hover:bg-bg-tertiary transition-colors",
                        disabled && "opacity-70",
                      )}
                      data-testid={`admin-broker-row-${b.id}`}
                    >
                      <td className="p-4">
                        <div className="text-white font-semibold text-[14px]">{b.name}</div>
                        <div className="text-text-secondary text-[12px] mt-1 max-w-[380px]">{b.description}</div>
                        {inversion && !disabled && (
                          <div className="mt-1 inline-flex items-center gap-1 text-warning text-[11px]">
                            <AlertTriangle size={11} /> PRO rate higher than Standard
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="inline-flex px-2 py-1 rounded-full text-[10px] font-semibold tracking-[0.06em] uppercase bg-bg-primary border border-border-default text-text-secondary">
                          {b.category}
                        </span>
                      </td>
                      <td className="p-4">
                        <PriceCell
                          value={b.price_standard_cents}
                          disabled={disabled}
                          onSave={(c) => patch(b, { price_standard_cents: c })}
                          testid={`admin-broker-std-${b.id}`}
                        />
                      </td>
                      <td className="p-4">
                        <PriceCell
                          value={b.price_pro_cents}
                          disabled={disabled}
                          onSave={(c) => patch(b, { price_pro_cents: c })}
                          testid={`admin-broker-pro-${b.id}`}
                        />
                      </td>
                      <td className="p-4">
                        <JeaToggle
                          checked={b.enabled}
                          onChange={(next) => patch(b, { enabled: next })}
                          data-testid={`admin-broker-toggle-${b.id}`}
                        />
                      </td>
                      <td className="p-4 jea-date text-[13px]">{relativeTime(b.last_updated_at)}</td>
                      <td className="p-4 text-right">
                        <button
                          type="button"
                          onClick={() => setHistoryBroker(b)}
                          className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/50 transition-colors inline-flex items-center justify-center"
                          title="Change history"
                          data-testid={`admin-broker-history-${b.id}`}
                        >
                          <History size={13} />
                        </button>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </JeaCard>
      )}

      <HistoryModal broker={historyBroker} onClose={() => setHistoryBroker(null)} />
    </div>
  );
};

export default AdminBrokersPage;
